import Phaser from 'phaser';
import { DialogueLine, DialogueSet } from '../data/dialogues';
import { DialogueBox } from '../ui/DialogueBox';
import { setZoneHeaderVisible } from '../ui/ZoneHudHeader';
import { paginateDialogueText } from '../utils/dialoguePaging';
import { AudioSystem } from './AudioSystem';
import { InputSystem } from './InputSystem';

export class DialogueSystem {
  private dialogueBox: DialogueBox;
  private inputSystem?: InputSystem;
  private currentLines: DialogueLine[] = [];
  private currentIndex = 0;
  private onComplete?: () => void;
  private active = false;

  constructor(scene: Phaser.Scene, inputSystem?: InputSystem) {
    this.inputSystem = inputSystem;
    this.dialogueBox = new DialogueBox(scene);
  }

  isActive(): boolean {
    return this.active;
  }

  show(dialogue: DialogueSet, onComplete?: () => void): void {
    this.currentLines = this.expandLines(dialogue.lines);
    this.currentIndex = 0;
    this.onComplete = onComplete;
    this.active = true;
    setZoneHeaderVisible(false);
    this.showCurrentLine();
  }

  showLines(lines: DialogueLine[], onComplete?: () => void): void {
    this.currentLines = this.expandLines(lines);
    this.currentIndex = 0;
    this.onComplete = onComplete;
    this.active = true;
    setZoneHeaderVisible(false);
    this.showCurrentLine();
  }

  update(): void {
    if (!this.active || !this.inputSystem) return;
    if (this.inputSystem.isActionJustPressed()) {
      this.advance();
    }
  }

  private expandLines(lines: DialogueLine[]): DialogueLine[] {
    const result: DialogueLine[] = [];
    const layout = this.dialogueBox.getTextLayout();
    const fits = (text: string) => this.dialogueBox.textFits(text, layout);

    for (const line of lines) {
      const pages = paginateDialogueText(line.text, fits);
      for (const text of pages) {
        result.push({ speaker: line.speaker, text });
      }
    }

    return result.length ? result : [{ speaker: 'Narator', text: ' ' }];
  }

  private showCurrentLine(): void {
    if (this.currentIndex >= this.currentLines.length) {
      this.close();
      return;
    }
    const line = this.currentLines[this.currentIndex];
    this.dialogueBox.show(line.speaker, line.text, () => this.advance());
  }

  private advance(): void {
    if (!this.active) return;
    AudioSystem.playSfx('DIALOGUE');
    this.currentIndex++;
    this.showCurrentLine();
  }

  private close(): void {
    this.dialogueBox.hide();
    const callback = this.onComplete;
    this.onComplete = undefined;
    this.active = false;
    setZoneHeaderVisible(true);
    callback?.();
  }

  hide(): void {
    this.dialogueBox.hide();
    this.active = false;
    setZoneHeaderVisible(true);
  }

  destroy(): void {
    this.hide();
    this.dialogueBox.destroy();
  }
}
